import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtMoney, imgUrl, PLACEHOLDER_IMGS } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Settings2, AlertTriangle, TrendingUp, TrendingDown, Building2, FileDown, ChevronDown, Sparkles } from "lucide-react";
import FlujoEfectivo from "../components/FlujoEfectivo";
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, ReferenceLine, Legend,
} from "recharts";

const SEM = { verde: "#4ADE80", amarillo: "#FBBF24", rojo: "#F87171", gris: "#6B7280" };
const CREMA = "#EAD8C0";
const ORO = "#C9A96A";

function fmtM(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toLocaleString("es-MX", { maximumFractionDigits: 1 })} M`;
  if (abs >= 1e3) return `$${(n / 1e3).toLocaleString("es-MX", { maximumFractionDigits: 0 })} K`;
  return fmtMoney(n);
}

function fmtKpi(k) {
  if (k.unidad === "$/m²") return `${fmtMoney(k.real)}/m²`;
  if (k.unidad === "$") return fmtM(k.real);
  if (k.unidad === "%") return `${(k.real ?? 0).toLocaleString("es-MX", { maximumFractionDigits: 1 })}%`;
  if (k.unidad === "x") return `${(k.real ?? 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}x`;
  return k.real;
}

function Semaforo({ color, size = 10 }) {
  return <span className="rounded-full inline-block flex-shrink-0" style={{ width: size, height: size, background: SEM[color] || SEM.gris, boxShadow: `0 0 8px ${SEM[color] || SEM.gris}66` }} />;
}

function KpiCard({ k }) {
  const delta = k.cumplimiento_pct == null ? null : Math.round((k.cumplimiento_pct - 100) * 10) / 10;
  return (
    <div data-testid={`ceo-kpi-${k.clave}`} className="bg-[#17151F]/0 bg-gradient-to-b from-[#1B1915] to-[#141210] border border-[#2C2822] rounded-xl p-4 flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-semibold leading-tight">{k.nombre}</p>
        <Semaforo color={k.color} />
      </div>
      <p className="font-display text-2xl font-bold text-[#F5EFE4]" data-testid={`ceo-kpi-${k.clave}-valor`}>{fmtKpi(k)}</p>
      {delta != null ? (
        <p className={`text-[11px] flex items-center gap-1 ${delta >= 0 ? "text-emerald-400" : k.color === "amarillo" ? "text-amber-400" : "text-red-400"}`}>
          {delta >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
          {delta >= 0 ? "+" : ""}{delta}% vs. plan
        </p>
      ) : (
        <p className="text-[11px] text-white/30">Sin plan capturado</p>
      )}
      {k.detalle && <p className="text-[10px] text-white/25 truncate" title={k.detalle}>{k.detalle}</p>}
    </div>
  );
}

function Panel({ title, children, className = "", testId }) {
  return (
    <div data-testid={testId} className={`bg-gradient-to-b from-[#1B1915] to-[#141210] border border-[#2C2822] rounded-xl p-5 ${className}`}>
      <h3 className="font-display text-lg font-bold text-[#F5EFE4] mb-4">{title}</h3>
      {children}
    </div>
  );
}

function SemaforoModal({ open, onClose, semaforo, onSaved }) {
  const [f, setF] = useState({ verde: 95, amarillo: 85 });
  useEffect(() => { if (semaforo) setF(semaforo); }, [semaforo, open]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/ceo/semaforo", { verde: Number(f.verde), amarillo: Number(f.amarillo) });
      toast.success("Umbrales del semáforo actualizados");
      onSaved(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="semaforo-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">Umbrales del semáforo</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Cumplimiento vs plan: 🟢 verde si ≥ umbral verde, 🟡 amarillo si ≥ umbral amarillo, 🔴 rojo por debajo.</p>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">🟢 Verde ≥ (%)</Label>
              <Input data-testid="semaforo-verde-input" type="number" step="any" value={f.verde} onChange={(e) => setF({ ...f, verde: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">🟡 Amarillo ≥ (%)</Label>
              <Input data-testid="semaforo-amarillo-input" type="number" step="any" value={f.amarillo} onChange={(e) => setF({ ...f, amarillo: e.target.value })} className="mt-1" />
            </div>
          </div>
          <Button data-testid="semaforo-guardar-button" type="submit" className="w-full bg-[#002B49] hover:bg-[#00395f]">Guardar</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const legendFmt = (v) => <span style={{ color: "#8B857A", fontSize: 11 }}>{v === "real_acum" ? "Real" : "Plan"}</span>;

function waterfallData(w) {
  if (!w) return [];
  const pasos = [];
  pasos.push({ nombre: "Plan", base: 0, valor: w.plan, color: CREMA });
  let run = w.plan;
  [["Δ Costo", w.delta_costo], ["Δ Ventas", w.delta_ventas], ["Otros", w.delta_otros]].forEach(([n, d]) => {
    pasos.push({ nombre: n, base: d >= 0 ? run : run + d, valor: Math.abs(d), color: d >= 0 ? "#4ADE80" : "#F87171", signo: d });
    run += d;
  });
  pasos.push({ nombre: "Forecast", base: 0, valor: w.forecast, color: ORO });
  return pasos;
}

export default function CeoDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [perFlujo, setPerFlujo] = useState({ periodo: "mes", desde: "", hasta: "" });
  const [proyectos, setProyectos] = useState([]);
  const [sel, setSel] = useState([]); // [] = todos
  const [selOpen, setSelOpen] = useState(false);
  const [comp, setComp] = useState([]);
  const [insights, setInsights] = useState(null);
  const [genInsights, setGenInsights] = useState(false);

  const qParam = (s) => (s.length ? `?projects=${s.join(",")}` : "");
  const load = (s = sel) => {
    api.get(`/ceo/dashboard${qParam(s)}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/comercial/comparativo${qParam(s)}`).then((r) => setComp(r.data.proyectos)).catch(() => {});
  };
  useEffect(() => {
    if (["admin", "financiero"].includes(user?.role)) {
      load([]);
      api.get("/properties").then((r) => setProyectos(r.data || [])).catch(() => {});
    }
  }, []); // eslint-disable-line

  const toggleProyecto = (pid) => {
    const next = sel.includes(pid) ? sel.filter((x) => x !== pid) : [...sel, pid];
    const efectivo = next.length === proyectos.length ? [] : next; // todos marcados = todos
    setSel(efectivo);
    setD(null);
    load(efectivo);
  };
  const verTodos = () => { setSel([]); setD(null); setInsights(null); load([]); };

  const generarInsights = async () => {
    setGenInsights(true);
    try {
      const r = await api.post(`/comercial/comparativo/insights${qParam(sel)}`);
      setInsights(r.data.insights);
    } catch (e) { toast.error(apiError(e)); }
    setGenInsights(false);
  };

  const exportarPdf = async () => {
    setDescargando(true);
    try {
      const extra = new URLSearchParams({ periodo: perFlujo.periodo });
      if (perFlujo.periodo === "rango") { extra.set("desde", perFlujo.desde || ""); extra.set("hasta", perFlujo.hasta || ""); }
      const base = qParam(sel);
      const r = await api.get(`/ceo/reporte-pdf${base ? `${base}&` : "?"}${extra.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resumen_ejecutivo_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Reporte Ejecutivo descargado");
    } catch (e) { toast.error("No se pudo generar el PDF"); }
    setDescargando(false);
  };

  if (user && !["admin", "financiero"].includes(user.role)) return <Navigate to="/" replace />;

  const fvp = d?.forecast_vs_plan;
  const dona = d ? [
    { name: "Vendidas", value: d.unidades.vendidas, color: "#4ADE80" },
    { name: "Apartadas", value: d.unidades.apartadas, color: "#FBBF24" },
    { name: "Disponibles", value: d.unidades.disponibles, color: CREMA },
    { name: "Bloqueadas", value: d.unidades.bloqueadas, color: "#F87171" },
  ].filter((x) => x.value > 0) : [];
  const wf = waterfallData(d?.waterfall);

  return (
    <div className="min-h-screen bg-[#0E0D0B] text-white pb-16" data-testid="ceo-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#0E0D0B]/90 backdrop-blur border-b border-[#2C2822] px-4 sm:px-10 py-4 flex items-center gap-4">
        <Link to="/" data-testid="ceo-volver-link" className="text-white/40 hover:text-white flex items-center gap-2 text-sm">
          <ArrowLeft size={16} /> <span className="hidden sm:inline">Proyectos</span>
        </Link>
        <div className="flex-1 min-w-0 text-center">
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#C9A96A]">{d?.empresa?.nombre || "—"} · Dirección General</p>
        </div>
        <button data-testid="ceo-exportar-pdf-button" onClick={exportarPdf} disabled={descargando || !d}
          title="Descargar Resumen Ejecutivo en PDF para inversionistas"
          className="flex items-center gap-2 text-[#EAD8C0] hover:text-white transition-colors px-3 py-1.5 rounded-md border border-[#EAD8C0]/30 hover:border-[#EAD8C0]/70 text-xs font-bold uppercase tracking-wider disabled:opacity-40">
          <FileDown size={14} /> <span className="hidden sm:inline">{descargando ? "Generando..." : "Exportar PDF"}</span>
        </button>
        {user?.role === "admin" && (
          <button data-testid="ceo-config-button" onClick={() => setCfgOpen(true)} title="Configurar umbrales del semáforo"
            className="text-white/40 hover:text-white p-2"><Settings2 size={17} /></button>
        )}
      </header>

      <div className="px-4 sm:px-10 max-w-[1500px] mx-auto">
        <div className="pt-8 pb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#F5EFE4]">Resumen Ejecutivo</h1>
            <p className="text-white/40 mt-1">Visión completa. Decisiones con claridad.</p>
          </div>
          {/* Selector de proyectos */}
          <div className="relative" data-testid="ceo-selector-proyectos">
            <button data-testid="ceo-selector-boton" onClick={() => setSelOpen(!selOpen)}
              className="flex items-center gap-2 bg-[#1B1915] border border-[#2C2822] hover:border-[#C9A96A]/50 rounded-lg px-4 py-2.5 text-sm text-[#EAD8C0] transition-colors min-w-[220px] justify-between">
              <span className="truncate">
                {sel.length === 0 ? "Todos los proyectos" :
                  sel.length === 1 ? (proyectos.find((p) => p.id === sel[0])?.nombre || "1 proyecto") :
                  `${sel.length} proyectos seleccionados`}
              </span>
              <ChevronDown size={15} className={`flex-shrink-0 transition-transform ${selOpen ? "rotate-180" : ""}`} />
            </button>
            {selOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setSelOpen(false)} />
                <div className="absolute right-0 mt-2 w-72 bg-[#1B1915] border border-[#2C2822] rounded-lg shadow-2xl z-40 py-1 max-h-80 overflow-y-auto">
                  <button data-testid="ceo-sel-todos" onClick={() => { verTodos(); setSelOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 flex items-center gap-2 ${sel.length === 0 ? "text-[#C9A96A] font-bold" : "text-white/70"}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${sel.length === 0 ? "bg-[#C9A96A] border-[#C9A96A] text-black" : "border-white/25"}`}>{sel.length === 0 ? "✓" : ""}</span>
                    Todos los proyectos
                  </button>
                  <div className="border-t border-white/5 my-1" />
                  {proyectos.map((p) => {
                    const activo = sel.includes(p.id);
                    return (
                      <button key={p.id} data-testid={`ceo-sel-${p.id}`} onClick={() => toggleProyecto(p.id)}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 flex items-center gap-2 ${activo ? "text-[#EAD8C0]" : "text-white/60"}`}>
                        <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] flex-shrink-0 ${activo ? "bg-[#C9A96A] border-[#C9A96A] text-black" : "border-white/25"}`}>{activo ? "✓" : ""}</span>
                        <span className="truncate">{p.nombre}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
        {sel.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-5 -mt-2" data-testid="ceo-chips-seleccion">
            {sel.map((pid) => {
              const p = proyectos.find((x) => x.id === pid);
              return (
                <span key={pid} className="flex items-center gap-1.5 text-[11px] bg-[#C9A96A]/10 border border-[#C9A96A]/30 text-[#EAD8C0] rounded-full px-3 py-1">
                  {p?.nombre || pid}
                  <button onClick={() => toggleProyecto(pid)} className="text-[#C9A96A] hover:text-white">×</button>
                </span>
              );
            })}
            <button onClick={verTodos} className="text-[11px] text-white/40 hover:text-white underline">Ver todos</button>
          </div>
        )}

        {!d && <p className="text-white/40 py-20 text-center">Cargando tablero...</p>}

        {d && (
          <>
            {/* Forecast vs Business Plan */}
            {fvp && (
              <div data-testid="forecast-vs-plan" className="rounded-xl border p-5 sm:p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-4"
                style={{ borderColor: SEM[fvp.color] + "55", background: `linear-gradient(90deg, ${SEM[fvp.color]}14, transparent 60%)` }}>
                <div className="flex items-center gap-3">
                  <AlertTriangle size={22} style={{ color: SEM[fvp.color] }} />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">Forecast vs Business Plan</p>
                    <p className="text-xs text-white/35">¿Cuánto vamos a ganar hoy vs lo aprobado?</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2 sm:ml-auto">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-white/40">Utilidad forecast</p>
                    <p className="font-display text-3xl font-bold" style={{ color: SEM[fvp.color] }} data-testid="fvp-forecast">{fmtM(fvp.utilidad_forecast)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-white/40">Plan aprobado</p>
                    <p className="font-display text-2xl font-bold text-[#EAD8C0]">{fmtM(fvp.utilidad_plan)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-white/40">Desviación</p>
                    <p className={`font-display text-2xl font-bold ${fvp.delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {fvp.delta >= 0 ? "+" : ""}{fmtM(fvp.delta)}{fvp.cumplimiento_pct != null && <span className="text-sm font-normal text-white/40 ml-2">({fvp.cumplimiento_pct}%)</span>}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Flujo de efectivo (selector propio de periodo) */}
            <FlujoEfectivo sel={sel} proyectos={proyectos} onPeriodo={setPerFlujo} />

            {/* 10 KPIs */}
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold mb-2">Indicadores acumulados a la fecha</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="ceo-kpis-grid">
              {d.kpis.map((k) => <KpiCard key={k.clave} k={k} />)}
            </div>

            {/* Gráficas principales */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <Panel title="Evolución de Ventas (acumulado)" testId="panel-evolucion" className="lg:col-span-1">
                {d.evolucion_ventas.length === 0 ? <p className="text-sm text-white/30">Sin ventas registradas aún.</p> : (
                  <ResponsiveContainer width="100%" height={230}>
                    <LineChart data={d.evolucion_ventas}>
                      <XAxis dataKey="mes" tick={{ fontSize: 9, fill: "#8B857A" }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fontSize: 9, fill: "#8B857A" }} width={45} tickFormatter={(v) => `$${(v / 1e6).toFixed(0)}M`} />
                      <RTooltip formatter={(v, n) => [fmtM(v), n === "real_acum" ? "Real" : "Plan"]}
                        contentStyle={{ background: "#1B1915", border: "1px solid #2C2822", borderRadius: 8, fontSize: 12, color: "#F5EFE4" }} />
                      <Legend formatter={legendFmt} />
                      <Line type="monotone" dataKey="real_acum" stroke={ORO} strokeWidth={2.5} dot={{ r: 2.5, fill: ORO }} />
                      <Line type="monotone" dataKey="plan_acum" stroke="#8B857A" strokeWidth={1.5} strokeDasharray="6 4" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Panel>

              <Panel title="Estado de Unidades" testId="panel-unidades">
                {d.unidades.total === 0 ? <p className="text-sm text-white/30">Sin inventario comercial. Captura unidades en el módulo Comercialización.</p> : (
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <PieChart width={170} height={170}>
                        <Pie data={dona} dataKey="value" innerRadius={55} outerRadius={80} paddingAngle={2} strokeWidth={0}>
                          {dona.map((x) => <Cell key={x.name} fill={x.color} />)}
                        </Pie>
                      </PieChart>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <p className="font-display text-2xl font-bold text-[#F5EFE4]">{d.unidades.total}</p>
                        <p className="text-[9px] uppercase tracking-wider text-white/40">Total</p>
                      </div>
                    </div>
                    <div className="space-y-2 flex-1">
                      {[["Vendidas", d.unidades.vendidas, "#4ADE80"], ["Apartadas", d.unidades.apartadas, "#FBBF24"],
                        ["Disponibles", d.unidades.disponibles, CREMA], ["Bloqueadas", d.unidades.bloqueadas, "#F87171"]].map(([n, v, c]) => (
                        <div key={n} className="flex items-center gap-2 text-xs">
                          <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                          <span className="text-white/50 flex-1">{n}</span>
                          <span className="font-bold text-[#F5EFE4]">{v}</span>
                          <span className="text-white/30 w-10 text-right">{d.unidades.total ? Math.round(v / d.unidades.total * 100) : 0}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Panel>

              <Panel title="Utilidad Esperada al Cierre" testId="panel-waterfall">
                {!d.waterfall || (d.waterfall.plan === 0 && d.waterfall.forecast === 0) ? (
                  <p className="text-sm text-white/30">Captura el Plan de Negocio de tus proyectos para ver el puente Plan → Forecast.</p>
                ) : (
                  <>
                    <p className="font-display text-2xl font-bold text-[#F5EFE4] mb-2">{fmtM(d.waterfall.forecast)}
                      <span className="text-sm font-normal text-white/35 ml-2">vs. {fmtM(d.waterfall.plan)} (plan)</span></p>
                    <ResponsiveContainer width="100%" height={185}>
                      <BarChart data={wf}>
                        <XAxis dataKey="nombre" tick={{ fontSize: 9, fill: "#8B857A" }} />
                        <YAxis hide />
                        <RTooltip cursor={{ fill: "#ffffff08" }}
                          formatter={(v, n, p) => n === "valor" ? [fmtM(p.payload.signo != null ? p.payload.signo : v), p.payload.nombre] : [null, null]}
                          contentStyle={{ background: "#1B1915", border: "1px solid #2C2822", borderRadius: 8, fontSize: 12, color: "#F5EFE4" }} />
                        <ReferenceLine y={0} stroke="#2C2822" />
                        <Bar dataKey="base" stackId="w" fill="transparent" />
                        <Bar dataKey="valor" stackId="w" radius={[3, 3, 0, 0]}>
                          {wf.map((x, i) => <Cell key={i} fill={x.color} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )}
              </Panel>
            </div>

            {/* Portafolio */}
            <Panel title="Portafolio de Proyectos" testId="panel-portafolio" className="mb-6">
              {d.portafolio.length === 0 ? <p className="text-sm text-white/30">Sin proyectos.</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  {d.portafolio.map((p, i) => (
                    <Link to={`/p/${p.id}/comercializacion`} key={p.id} data-testid={`portafolio-${p.id}`}
                      className="group rounded-lg overflow-hidden border border-[#2C2822] bg-[#141210] hover:border-[#C9A96A]/50 transition-colors">
                      <div className="h-28 overflow-hidden bg-[#1B1915]">
                        <img src={p.imagen ? imgUrl(p.imagen) : PLACEHOLDER_IMGS[i % PLACEHOLDER_IMGS.length]} alt={p.nombre}
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                      </div>
                      <div className="p-3">
                        <p className="font-display font-bold text-[#F5EFE4] text-sm truncate">{p.nombre}</p>
                        <p className="text-[10px] text-white/35 truncate mb-2">{p.ubicacion || "—"}</p>
                        {[["Unidades", p.unidades], ["Vendidas", `${p.vendidas} (${p.absorcion_pct}%)`],
                          ["Ventas", fmtM(p.ventas)], ["Avance físico", `${p.avance_fisico}%`], ["IRR", `${p.irr}%`]].map(([l, v]) => (
                          <div key={l} className="flex justify-between text-[11px] py-0.5 border-b border-white/5 last:border-0">
                            <span className="text-white/40">{l}</span><span className="text-[#EAD8C0] font-semibold">{v}</span>
                          </div>
                        ))}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Panel>

            {/* Comparativo de proyectos (Sprint 3) */}
            <Panel title="Comparativo de Proyectos" testId="panel-comparativo" className="mb-6">
              {comp.length < 2 ? <p className="text-sm text-white/30">Selecciona al menos 2 proyectos (o Todos) para comparar lado a lado.</p> : (
                <div className="space-y-5">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[900px]">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-white/35 border-b border-white/10">
                          <th className="py-2 pr-3">Proyecto</th>
                          {[["Unidades", "unidades", 0], ["Vendidas", "vendidas", 0], ["Absorción", "absorcion_pct", 1],
                            ["Ventas", "ventas", 1], ["Precio/m²", "precio_prom_m2", 1], ["Velocidad", "velocidad_mes", 1],
                            ["Meses inv.", "meses_inventario", -1], ["Cobrado", "cobrado", 1], ["Vencido", "vencido", -1]].map(([l]) => (
                            <th key={l} className="py-2 px-2 text-right">{l}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {comp.map((p) => {
                          const cell = (campo, dir, fmt) => {
                            const vals = comp.map((x) => x[campo]).filter((v) => v != null);
                            const v = p[campo];
                            let cls = "text-white/70";
                            if (dir !== 0 && vals.length > 1 && v != null) {
                              const best = dir > 0 ? Math.max(...vals) : Math.min(...vals);
                              const worst = dir > 0 ? Math.min(...vals) : Math.max(...vals);
                              if (v === best && best !== worst) cls = "text-[#C9A96A] font-bold";
                              else if (v === worst && best !== worst) cls = "text-red-400";
                            }
                            return <td key={campo} className={`py-2.5 px-2 text-right ${cls}`}>{v == null ? "—" : fmt(v)}</td>;
                          };
                          return (
                            <tr key={p.id} className="border-b border-white/5" data-testid={`comp-row-${p.id}`}>
                              <td className="py-2.5 pr-3 font-semibold text-[#F5EFE4]">{p.nombre}</td>
                              {cell("unidades", 0, (v) => v)}
                              {cell("vendidas", 0, (v) => v)}
                              {cell("absorcion_pct", 1, (v) => `${v}%`)}
                              {cell("ventas", 1, fmtM)}
                              {cell("precio_prom_m2", 1, (v) => v ? `$${Number(v).toLocaleString("es-MX")}` : "—")}
                              {cell("velocidad_mes", 1, (v) => `${v} u/mes`)}
                              {cell("meses_inventario", -1, (v) => `${v} m`)}
                              {cell("cobrado", 1, fmtM)}
                              {cell("vencido", -1, fmtM)}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <p className="text-[10px] text-white/25 mt-1">Dorado = mejor del grupo · Rojo = peor. Velocidad = unidades vendidas por mes de operación.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold mb-2">Curva de absorción (% por mes desde inicio)</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <LineChart>
                          <XAxis dataKey="mes_n" type="number" domain={[1, "dataMax"]} tick={{ fontSize: 9, fill: "#8B857A" }} allowDuplicatedCategory={false} />
                          <YAxis tick={{ fontSize: 9, fill: "#8B857A" }} width={32} unit="%" />
                          <RTooltip formatter={(v) => [`${v}%`, "Absorción"]} labelFormatter={(l) => `Mes ${l}`}
                            contentStyle={{ background: "#1B1915", border: "1px solid #2C2822", borderRadius: 8, fontSize: 12, color: "#F5EFE4" }} />
                          {comp.map((p, i) => (
                            <Line key={p.id} data={p.curva} dataKey="absorcion" name={p.nombre} type="monotone"
                              stroke={[ORO, "#5B8A9A", "#4ADE80", "#F87171", CREMA, "#A78BFA"][i % 6]} strokeWidth={2} dot={false} />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                      <div className="flex flex-wrap gap-3 mt-1">
                        {comp.map((p, i) => (
                          <span key={p.id} className="flex items-center gap-1.5 text-[10px] text-white/50">
                            <span className="w-2 h-2 rounded-full" style={{ background: [ORO, "#5B8A9A", "#4ADE80", "#F87171", CREMA, "#A78BFA"][i % 6] }} />{p.nombre}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Insights del comparativo</p>
                        <button data-testid="btn-insights" onClick={generarInsights} disabled={genInsights}
                          className="flex items-center gap-1.5 text-[11px] text-[#EAD8C0] border border-[#EAD8C0]/30 hover:border-[#EAD8C0]/70 rounded-md px-2.5 py-1 disabled:opacity-40">
                          <Sparkles size={11} /> {genInsights ? "Analizando..." : "Generar con IA"}
                        </button>
                      </div>
                      {insights ? (
                        <div data-testid="insights-box" className="text-xs text-white/70 whitespace-pre-line bg-white/5 border border-white/10 rounded-lg p-3 max-h-52 overflow-y-auto leading-relaxed">{insights}</div>
                      ) : (
                        <p className="text-xs text-white/25">La IA analiza velocidad de venta, absorción, precios y cobranza para decirte qué proyecto va mejor, cuál necesita atención y qué hacer.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Panel>

            {/* Construcción + cobranza */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Panel title="Avance de Construcción" testId="panel-construccion" className="lg:col-span-2">
                {d.construccion.length === 0 ? <p className="text-sm text-white/30">Sin proyectos.</p> : (
                  <div className="space-y-4">
                    <div className="flex gap-4 text-[10px] uppercase tracking-wider text-white/40">
                      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: ORO }} /> Físico</span>
                      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#5B8A9A]" /> Financiero</span>
                    </div>
                    {d.construccion.map((c) => (
                      <div key={c.nombre}>
                        <p className="text-xs text-white/60 mb-1.5">{c.nombre}</p>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${Math.min(c.fisico, 100)}%`, background: ORO }} />
                            </div>
                            <span className="text-[10px] text-[#EAD8C0] w-9 text-right">{c.fisico}%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                              <div className="h-full rounded-full bg-[#5B8A9A]" style={{ width: `${Math.min(c.financiero, 100)}%` }} />
                            </div>
                            <span className="text-[10px] text-white/40 w-9 text-right">{c.financiero}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
              <Panel title="Cobranza consolidada" testId="panel-cobranza">
                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-white/40">Cobrado</p>
                    <p className="font-display text-3xl font-bold text-emerald-400">{fmtM(d.cobrado)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-white/40">Por cobrar</p>
                    <p className="font-display text-2xl font-bold text-[#EAD8C0]">{fmtM(d.por_cobrar)}</p>
                  </div>
                  <div className="pt-2 border-t border-white/5 flex items-center gap-2 text-xs text-white/30">
                    <Building2 size={13} /> {d.portafolio.length} proyecto{d.portafolio.length !== 1 ? "s" : ""} en el portafolio
                  </div>
                </div>
              </Panel>
            </div>
          </>
        )}
      </div>

      <SemaforoModal open={cfgOpen} onClose={() => setCfgOpen(false)} semaforo={d?.semaforo} onSaved={load} />
    </div>
  );
}
